import React, { useRef } from 'react';
import {
  Chart as ChartJS,
  LinearScale,
  TimeSeriesScale,
  PointElement,
  LineElement,
  Tooltip,
  Filler,
  Legend,
} from 'chart.js';
import 'chartjs-adapter-moment';
import { Line } from 'react-chartjs-2';
import { formatCurrency, toQuarter } from '../../helpers/format';
import useBalanceSheets from '../../hooks/useBalanceSheets';

const BalanceSheetChart = ({ stock }) => {
  const { balanceSheets } = useBalanceSheets(stock.id);

  ChartJS.register(
    LinearScale,
    TimeSeriesScale,
    PointElement,
    LineElement,
    Tooltip,
    Filler,
    Legend
  );

  const chartRef = useRef(null);

  if (!balanceSheets) return null;

  return <Line options={chartOptions()} data={data(balanceSheets)} ref={chartRef} />;
};

const COLOR_ASSETS = [0, 95, 115];
const COLOR_LIABILITIES = [231, 111, 81];
const COLOR_EQUITY = [148, 210, 189];

function data(balanceSheets) {
  const [labels, assets, liabilities, equity] = formatData(balanceSheets);

  return {
    labels: labels,
    datasets: [
      {
        label: 'Total Assets',
        data: assets,
        borderColor: `rgb(${COLOR_ASSETS.join(',')})`,
        borderWidth: 3,
        fill: true,
        backgroundColor: (context) => {
          const ctx = context.chart.ctx;
          let grad = ctx.createLinearGradient(0, 0, 0, 360);
          grad.addColorStop(0, `rgba(${COLOR_ASSETS.join(',')}, 0.05)`);
          grad.addColorStop(1, `rgba(${COLOR_ASSETS.join(',')}, 0)`);
          return grad;
        },
      },
      {
        label: 'Total Liabilities',
        data: liabilities,
        borderColor: `rgb(${COLOR_LIABILITIES.join(',')})`,
        borderWidth: 3,
        fill: true,
        backgroundColor: (context) => {
          const ctx = context.chart.ctx;
          let grad = ctx.createLinearGradient(0, 0, 0, 360);
          grad.addColorStop(0, `rgba(${COLOR_LIABILITIES.join(',')}, 0.05)`);
          grad.addColorStop(1, `rgba(${COLOR_LIABILITIES.join(',')}, 0)`);
          return grad;
        },
      },
      {
        label: 'Shareholder Equity',
        data: equity,
        borderColor: `rgb(${COLOR_EQUITY.join(',')})`,
        borderWidth: 3,
        fill: true,
        backgroundColor: (context) => {
          const ctx = context.chart.ctx;
          let grad = ctx.createLinearGradient(0, 0, 0, 360);
          grad.addColorStop(0, `rgba(${COLOR_EQUITY.join(',')}, 0.05)`);
          grad.addColorStop(1, `rgba(${COLOR_EQUITY.join(',')}, 0)`);
          return grad;
        },
      },
    ],
  };
}

export default BalanceSheetChart;

const formatData = (balanceSheets) => {
  let labels = [];
  let assets = [];
  let liabilities = [];
  let equity = [];

  for (let i = 0; i < balanceSheets.length; i++) {
    const date = new Date(balanceSheets[i].fiscal_date);
    labels.push(date);
    assets.push(balanceSheets[i].total_assets);
    liabilities.push(balanceSheets[i].total_liabilities);
    equity.push(balanceSheets[i].shareholder_equity);
  }

  return [labels, assets, liabilities, equity];
};

function currency(value) {
  return `${value < 0 ? '-' : ''}$${formatCurrency(Math.abs(value), { compact: true })}`;
}

function chartOptions() {
  return {
    responsive: true,
    maintainAspectRatio: false,
    pointStyle: false,
    animation: {
      duration: 0,
    },
    elements: {
      line: {
        tension: 0.1
      }
    },
    scales: {
      x: {
        type: 'timeseries',
        time: {
          tooltipFormat: 'MMM DD, YYYY',
          displayFormats: {
            month: 'MMM'
          },
          minUnit: 'quarter',
        },
        ticks: {
          maxRotation: 0,
          major: {
            enabled: true
          },
          font: function (context) {
            return context.tick && context.tick.major ? { weight: '700' } : undefined;
          },
        },
        grid: {
          display: false
        },
        border: {
          display: false,
        },
      },
      y: {
        grid: {
          display: false
        },
        border: {
          display: false,
        },
        ticks: {
          callback: (value) => currency(value),
        },
      }
    },
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
        labels: {
          usePointStyle: true,
          pointStyleWidth: 10,
          pointStyle: 'line',
        },
      },
      tooltip: {
        mode: 'index',
        position: 'nearest',
        intersect: false,
        callbacks: {
          title: (context) => {
            return toQuarter(context[0].label);
          },
          label: (context) => {
            return `${context.dataset.label}: ${currency(context.raw)}`;
          },
          labelPointStyle: () => 'circle',
        },
        usePointStyle: true
      }
    }
  };
}